import { LitElement, html } from 'lit';
import { query, state } from 'lit/decorators.js';
import { renderGrid } from '@giphy/js-components';
import { IGif } from '@giphy/js-types';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { searchGifs, GifsResult } from '@amityeko/pbc-core/dist/utilities/giphy';

import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/search';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';
import type AmityekoSearch from '@amityeko/design-system/dist/elements/search';

import styles from './styles';

/**
 * @event rnr-gif-selector-back - Emitted when an image editor dialog is confirmed.
 * @event rnr-select-gif {{ gif: IGif }} - Emitted when a gif is selected.
 */
export default class RnRGifSelectorDialog extends LitElement {
  static styles = styles;

  @query('#grid') grid!: HTMLDivElement;
  @query('#dialog') dialog!: AmityekoDialog;
  @query('#search') search!: AmityekoSearch;

  @state()
  gridRemoveCallback?: Function;

  public show() {
    this.reset();
    this.dialog.show();
    this._renderGifGrid();
  }

  public reset() {
    this.search.reset();
  }

  private _onGifClick(gif: IGif) {
    emit(this, 'rnr-select-gif', { detail: { gif } });
    this.dialog.hide();
  }

  private _onGifMouseOver(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  private _onClickBack() {
    emit(this, 'rnr-gif-selector-back');
    this.dialog.hide();
  }

  private _renderGifGrid(term?: string) {
    this.gridRemoveCallback && this.gridRemoveCallback();

    const fetchGifs = (offset: number) => {
      return searchGifs(offset, term).then((data: GifsResult) => {
        /**
         * Note this logic - it is not to provide any functionality. It is to avoid error spam.
         * As soon as the Grid component mounts the gif, events are attached to it to track
         * something. In one place (onGifHover (component) => get-pingback-id (utils)),
         * the Grid starts to spam error "Cannot read properties of null (reading 'offsetWidth')".
         * The reason is that with Shadow DOM in play, Grid fails to obtain some elements
         * references that it needs to calculate sizes. It is unclear why and how critical it is
         * - but removing the listener did not cause any adverse effect.
         *
         * The logic below awaits when a new batch of gifs is downloaded, waits a period of 1s,
         * and then blocks the mouseover event by blocking its propagation from
         * the child element of the gif container.
         *
         * It is not the variation that is the most reliable. Still, the error can jump
         * thru once or twice, but we at least avoid mad spamming behavior. The other way
         * to bypass this issue is to build the Grid ourselves. What is possible but takes
         * time to provide the same quality as this Grid component offers.
         */
        setTimeout(() => {
          const gifInternalNodes = this.grid.querySelectorAll('.giphy-gif > div');
          Array.from(gifInternalNodes).forEach(gifInternalElem => {
            // At first, we remove the listener from the elements rendered in the previous
            // pagination.
            gifInternalElem.removeEventListener('mouseover', this._onGifMouseOver);
            gifInternalElem.addEventListener('mouseover', this._onGifMouseOver);
          });
        }, 1000);
        return data;
      });
    };

    this.gridRemoveCallback = renderGrid(
      {
        width: 432,
        fetchGifs,
        columns: 2,
        gutter: 2,
        noLink: true,
        hideAttribution: true,
        onGifClick: (gif: IGif) => {
          this._onGifClick(gif);
        },
      },
      this.grid,
    );
  }

  private _onSearch(e: CustomEvent) {
    const { term } = e.detail || {};
    this._renderGifGrid(term);
  }

  protected render() {
    return html`
      <amityeko-dialog
        id="dialog"
        label="Select GIF"
        ?hasBackButton=${true}
        @amityeko-back=${this._onClickBack}
        @amityeko-cancel=${this._onClickBack}
      >
        <amityeko-search
          id="search"
          placeholder="Search GIPHY"
          delay-time="2000"
          @amityeko-search-change=${this._onSearch}
        ></amityeko-search>
        <div id="grid" class="grid"></div>
        <div class="copyright"></div>
      </amityeko-dialog>
    `;
  }
}

if (!customElements.get('rnr-gif-selector-dialog')) {
  customElements.define('rnr-gif-selector-dialog', RnRGifSelectorDialog);
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-gif-selector-dialog': RnRGifSelectorDialog;
  }
}
